import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    List,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import { CheckBox, CheckBoxOutlineBlank, ExpandMore } from "@material-ui/icons"
import { useSnackbar, VariantType } from "notistack"
import React from "react"
import { CampoTextoImpressao, TextoImpressaoActionsTypes } from "../context/textoImpressaoActions"
import { TextoImpressaoInicial } from "../context/textoImpressaoInicial"
import { useTextoImpressaoContext } from "../context/useTextoImpressaoContext"

const CAMPOS_ORDENADOS = (Object.keys(TextoImpressaoInicial) as string[]).filter(
    k => k !== "parametrosGerais"
) as CampoTextoImpressao[]

const CAMPOS_COM_VALOR = new Set<CampoTextoImpressao>([
    "pagamento",
    "subtotal",
    "taxaDeEntrega",
    "desconto",
    "taxaAdicional",
    "total",
])

const LABELS: Record<string, string> = {
    bairro: "Bairro",
    avisoCartela: "Aviso Cartela",
    categoria: "Categoria",
    cidade: "Cidade",
    cliente: "Cliente",
    cpf: "CPF",
    data: "Data",
    desconto: "Desconto",
    grupoDeOpcao: "Grupo de Opção",
    idDoPedido: "ID do Pedido",
    cnpj: "CNPJ",
    nomeEstabelecimento: "Nome do Estabelecimento",
    numeroEstabelecimento: "Número do Estabelecimento",
    numeroRua: "Número",
    opcao: "Opção",
    observacaoProduto: "Obs. do Produto",
    observacaoPedido: "Obs. do Pedido",
    pagamento: "Pagamento",
    pedidosAnteriores: "Pedidos Anteriores",
    previsaoEntrega: "Previsão de Entrega",
    produto: "Produto",
    rua: "Rua",
    subtotal: "Subtotal",
    taxaAdicional: "Taxa Adicional",
    taxaDeEntrega: "Taxa de Entrega",
    telefone: "Telefone",
    troco: "Troco",
    trocoPara: "Troco Para",
    total: "Total",
}

function NumericTextField({
    label,
    value,
    min,
    max,
    defaultValue,
    onChange,
    fullWidth,
}: {
    label: string
    value: number
    min: number
    max: number
    defaultValue: number
    onChange: (value: number) => void
    fullWidth?: boolean
}) {
    const [localValue, setLocalValue] = React.useState<string>(String(value))

    React.useEffect(() => {
        setLocalValue(String(value))
    }, [value])

    const commit = (raw: string) => {
        const num =
            raw === "" ? defaultValue : Math.min(max, Math.max(min, Number(raw) || defaultValue))
        setLocalValue(String(num))
        onChange(num)
    }

    return (
        <TextField
            label={label}
            variant="outlined"
            size="small"
            type="number"
            fullWidth={fullWidth}
            value={localValue}
            onChange={e => setLocalValue(e.target.value)}
            onBlur={() => commit(localValue)}
        />
    )
}

export function Configuracoes() {
    const { textoImpressao, dispatchTexoImpressao, salvarConfiguracoes, carregando } =
        useTextoImpressaoContext()
    const { enqueueSnackbar } = useSnackbar()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const alertStart = (msg: string, tipo: VariantType | undefined) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const handleSalvarConfiguracoes = async () => {
        try {
            await salvarConfiguracoes()
            alertStart("Configurações salvas com sucesso!", "success")
        } catch (error: any) {
            alertStart(error?.message || "Erro ao salvar configurações.", "error")
        } finally {
        }
    }

    if (!textoImpressao) return null

    const pg = textoImpressao.parametrosGerais
    const updatePG = (propriedade: keyof typeof pg, valor: boolean | number | string) =>
        dispatchTexoImpressao({
            type: TextoImpressaoActionsTypes.ATUALIZAR_PARAMETROS_GERAIS,
            payload: { propriedade, valor },
        })

    return (
        <div
            style={{
                width: "100%",
                boxShadow: isMobile ? "none" : "0px 0px 10px rgba(0,0,0,0.2)",
                borderRadius: isMobile ? 0 : "0.4rem",
                padding: isMobile ? 0 : "1rem",
                overflow: "auto",
            }}
        >
            <Typography
                variant="h6"
                style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginBottom: "0.8rem",
                }}
            >
                Preferências de Impressão
            </Typography>

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.8rem",
                    marginBottom: "1rem",
                }}
            >
                <Button
                    variant="contained"
                    style={{ backgroundColor: "#D32F2F", color: "white" }}
                    disabled={carregando}
                    onClick={() =>
                        dispatchTexoImpressao({ type: TextoImpressaoActionsTypes.RESETAR })
                    }
                >
                    Resetar
                </Button>
                <Button
                    variant="contained"
                    style={{ backgroundColor: "#4CAF50", color: "white" }}
                    disabled={carregando}
                    onClick={() => handleSalvarConfiguracoes()}
                >
                    {carregando ? (
                        <CircularProgress size={20} style={{ color: "white" }} />
                    ) : (
                        "Atualizar"
                    )}
                </Button>
            </div>

            <Accordion defaultExpanded style={{ marginBottom: "0.5rem" }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography component="span" style={{ fontWeight: "bold" }}>
                        Parâmetros Gerais
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <InputLabel>Fonte Padrão</InputLabel>
                                <Select
                                    label="Fonte Padrão"
                                    value={pg.fonte ?? 0}
                                    onChange={e => updatePG("fonte", Number(e.target.value))}
                                >
                                    <MenuItem value={0}>Pequeno</MenuItem>
                                    <MenuItem value={1}>Médio</MenuItem>
                                    <MenuItem value={2}>Grande</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={6} md={4}>
                            <NumericTextField
                                label="Colunas"
                                fullWidth
                                value={pg.colunas ?? 40}
                                min={38}
                                max={48}
                                defaultValue={48}
                                onChange={v => updatePG("colunas", v)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6} md={4}>
                            <NumericTextField
                                label="Linhas Pós Impressão"
                                fullWidth
                                value={pg.qtdLinhasPosImpressao ?? 0}
                                min={0}
                                max={20}
                                defaultValue={0}
                                onChange={v => updatePG("qtdLinhasPosImpressao", v)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="Texto Início de Linha"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={pg.textoInicioLinha ?? ""}
                                onChange={e => updatePG("textoInicioLinha", e.target.value)}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={4}
                            style={{ display: "flex", alignItems: "center" }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={pg.negrito ?? false}
                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                        checkedIcon={<CheckBox fontSize="small" />}
                                        onChange={(_, checked) => updatePG("negrito", checked)}
                                    />
                                }
                                label="Negrito Padrão"
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={4}
                            style={{ display: "flex", alignItems: "center" }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={pg.exibirLogo ?? false}
                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                        checkedIcon={<CheckBox fontSize="small" />}
                                        onChange={(_, checked) => updatePG("exibirLogo", checked)}
                                    />
                                }
                                label="Logo"
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={4}
                            style={{ display: "flex", alignItems: "center" }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={pg.linhaAposProduto ?? false}
                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                        checkedIcon={<CheckBox fontSize="small" />}
                                        onChange={(_, checked) =>
                                            updatePG("linhaAposProduto", checked)
                                        }
                                    />
                                }
                                label="Linha após produto"
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <List>
                {CAMPOS_ORDENADOS.map(campoKey => {
                    const campo = campoKey
                    const config = textoImpressao[campoKey]
                    if (!config) return null

                    return (
                        <Accordion key={campo}>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls={`${campo}-content`}
                                id={`${campo}-header`}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography component="span">
                                        {LABELS[campo] ?? campo}
                                    </Typography>

                                    <Switch
                                        checked={config.ativo}
                                        onClick={e => e.stopPropagation()}
                                        onChange={() =>
                                            dispatchTexoImpressao({
                                                type: TextoImpressaoActionsTypes.TOGGLE_ATIVO,
                                                payload: { campo: campoKey },
                                            })
                                        }
                                    />
                                </div>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    <Grid item xs={6} sm={4}>
                                        <NumericTextField
                                            label="Sup. (linhas)"
                                            value={config.margemSuperior}
                                            min={0}
                                            max={10}
                                            defaultValue={0}
                                            onChange={v =>
                                                dispatchTexoImpressao({
                                                    type: TextoImpressaoActionsTypes.ATUALIZAR_CAMPO,
                                                    payload: {
                                                        campo: campoKey,
                                                        propriedade: "margemSuperior",
                                                        valor: v,
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <NumericTextField
                                            label="Inf. (linhas)"
                                            value={config.margemInferior}
                                            min={0}
                                            max={10}
                                            defaultValue={0}
                                            onChange={v =>
                                                dispatchTexoImpressao({
                                                    type: TextoImpressaoActionsTypes.ATUALIZAR_CAMPO,
                                                    payload: {
                                                        campo: campoKey,
                                                        propriedade: "margemInferior",
                                                        valor: v,
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <NumericTextField
                                            label="Esq. (espaços)"
                                            value={config.margemEsquerda}
                                            min={0}
                                            max={20}
                                            defaultValue={0}
                                            onChange={v =>
                                                dispatchTexoImpressao({
                                                    type: TextoImpressaoActionsTypes.ATUALIZAR_CAMPO,
                                                    payload: {
                                                        campo: campoKey,
                                                        propriedade: "margemEsquerda",
                                                        valor: v,
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <FormControl variant="outlined" size="small" fullWidth>
                                            <InputLabel>Fonte</InputLabel>
                                            <Select
                                                label="Fonte"
                                                value={config.fonte ?? 0}
                                                onClick={e => e.stopPropagation()}
                                                onChange={e =>
                                                    dispatchTexoImpressao({
                                                        type: TextoImpressaoActionsTypes.ATUALIZAR_CAMPO,
                                                        payload: {
                                                            campo: campoKey,
                                                            propriedade: "fonte",
                                                            valor: Number(e.target.value),
                                                        },
                                                    })
                                                }
                                            >
                                                <MenuItem value={0}>Pequeno</MenuItem>
                                                <MenuItem value={1}>Médio</MenuItem>
                                                <MenuItem value={2}>Grande</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={config.negrito}
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={(_, checked) =>
                                                        dispatchTexoImpressao({
                                                            type: TextoImpressaoActionsTypes.ATUALIZAR_CAMPO,
                                                            payload: {
                                                                campo: campoKey,
                                                                propriedade: "negrito",
                                                                valor: checked,
                                                            },
                                                        })
                                                    }
                                                />
                                            }
                                            label="Negrito"
                                        />
                                    </Grid>
                                    {/* {CAMPOS_COM_VALOR.has(campoKey) && (
                                        <Grid item xs={4} sm={4} md={4}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={config.valorDireita ?? false}
                                                        icon={
                                                            <CheckBoxOutlineBlank fontSize="small" />
                                                        }
                                                        checkedIcon={<CheckBox fontSize="small" />}
                                                        onClick={e => e.stopPropagation()}
                                                        onChange={(_, checked) =>
                                                            dispatchTexoImpressao({
                                                                type: TextoImpressaoActionsTypes.ATUALIZAR_CAMPO,
                                                                payload: {
                                                                    campo: campoKey,
                                                                    propriedade: "valorDireita",
                                                                    valor: checked,
                                                                },
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Valor à dir."
                                            />
                                        </Grid>
                                    )} */}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    )
                })}
            </List>
        </div>
    )
}
